import { useState } from "react";
import TicketsFromDingPay from "../../assets/imgs/tbdingpay-logo.svg";
import { toast } from "react-toastify";
import { signInWithEmailAndPassword, signOut } from "firebase/auth";
import { firebaseAuth, firestoredb } from "../../utils/firebase";
import { Loading } from "../../components/loading";
import { collection, getDocs, query, where } from "firebase/firestore";
import {
  getHourInMilliseconds,
  INTERNAL_ADMIN_IS_LOGGEDIN,
  INTERNAL_ADMIN_LOGIN_EXPIRY,
  LOGGED_IN_ADMIN_EVENTS,
} from "../../utils";
import { useNavigate } from "react-router-dom";

export const isEventAuthVerified = (eventId: string) => {
  const storedEventsIds = localStorage.getItem(LOGGED_IN_ADMIN_EVENTS);
  const alreadyLoggedInAdminEvents = storedEventsIds
    ? JSON.parse(storedEventsIds)
    : [];
  return !!alreadyLoggedInAdminEvents.find(
    (lsEvent: { data: string; expiresAt: number }) =>
      lsEvent.data === eventId && lsEvent.expiresAt > new Date().getTime()
  );
};

export function Login({ type = "admin" }: { type?: string }) {
  const isInternalAdmin = type === "internal-admin";
  const [showPassword, setShowPassword] = useState(false);
  const [uniqueCode, setUniqueCode] = useState("");
  const [adminCode, setAdminCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleInternalAdminLogin = async () => {
    if (!adminCode) return toast.error("Invalid Admin Code");
    setIsLoading(true);
    try {
      await signInWithEmailAndPassword(
        firebaseAuth,
        process.env.REACT_APP_FIREBASE_AUTH_EMAIL!,
        process.env.REACT_APP_FIREBASE_AUTH_PASSWORD!
      );
      const backdoorRef = collection(firestoredb, "backdoor");
      const backdoorSnap = await getDocs(
        query(backdoorRef, where("adminAccessCode", "==", adminCode))
      );
      if (backdoorSnap.empty) {
        setIsLoading(false);
        return toast.error("Invalid access code");
      }
      localStorage.setItem(INTERNAL_ADMIN_IS_LOGGEDIN, JSON.stringify(true));
      localStorage.setItem(
        INTERNAL_ADMIN_LOGIN_EXPIRY,
        JSON.stringify(new Date().getTime() + getHourInMilliseconds(24))
      );
      window.location.href = "/internal/dashboard";
    } catch (error) {
      setIsLoading(false);
      toast.error("Error logging in to your dashboard");
    }
  };

  const handleLogin = async () => {
    if (!uniqueCode) return toast.error("Invalid unique code");
    setIsLoading(true);
    try {
      await signInWithEmailAndPassword(
        firebaseAuth,
        process.env.REACT_APP_FIREBASE_AUTH_EMAIL!,
        process.env.REACT_APP_FIREBASE_AUTH_PASSWORD!
      );
      const scannerCodeConstraints = [where("scannerCode", "==", uniqueCode)];
      const eventCollectionRef = collection(firestoredb, "events");
      const eventQuerySnapshot = await getDocs(
        query(eventCollectionRef, ...scannerCodeConstraints)
      );
      if (eventQuerySnapshot.empty) {
        setIsLoading(false);
        await signOut(firebaseAuth); // because we signed in to get the data
        return toast.error(
          "This is an invalid unique code. Please use the correct code"
        );
      }
      const eventId = eventQuerySnapshot.docs[0].id;
      if (isEventAuthVerified(eventId)) {
        // this event is already logged in, so direct to dashboard page
        return navigate(`/admin/dashboard/${eventId}`);
      } else {
        // this event is not logged in, so save id to LS and direct to dashboard page
        const storedEventsIds = localStorage.getItem(LOGGED_IN_ADMIN_EVENTS);
        const alreadyLoggedInAdminEvents = storedEventsIds
          ? JSON.parse(storedEventsIds)
          : [];
        localStorage.setItem(
          LOGGED_IN_ADMIN_EVENTS,
          JSON.stringify([
            ...alreadyLoggedInAdminEvents,
            {
              data: eventId,
              expiresAt: new Date().getTime() + getHourInMilliseconds(24),
            },
          ])
        );
        toast.success("Login successful");
        return navigate(`/admin/dashboard/${eventId}`);
      }
    } catch (error) {
      toast.error("Unsuccessful login");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='container-fluid'>
      <div className='login login-container row'>
        <div className='col-12 col-md-4 login__form'>
          <div className='login__form-container'>
            <div className='login__form__logo'>
              <img
                src={TicketsFromDingPay}
                alt='logo'
                className='img-fluid logo'
              />
            </div>
            <h3>Welcome back!</h3>
            <h6>Continue with the unique code</h6>
            <div className='form-input-group'>
              <label>
                Unique Code <span className='required'>*</span>
              </label>
              <div className='form-input-group__input-password'>
                <input
                  placeholder={
                    isInternalAdmin
                      ? "Enter your admin code"
                      : "Enter your unique code here"
                  }
                  type={showPassword ? "text" : "password"}
                  onChange={({ target: { value } }) => {
                    if (isInternalAdmin) return setAdminCode(value);
                    setUniqueCode(value);
                  }}
                />
                <div className='form-input-group__input-password__icon'>
                  {!showPassword ? (
                    <i
                      className='bi bi-eye'
                      onClick={() => setShowPassword(!showPassword)}
                    />
                  ) : (
                    <i
                      className='bi bi-eye-slash'
                      onClick={() => setShowPassword(!showPassword)}
                    />
                  )}
                </div>
              </div>
            </div>
            <button
              className='login__form-container__button'
              disabled={
                (isInternalAdmin
                  ? adminCode.length === 0
                  : uniqueCode.length === 0) || isLoading
              }
              onClick={isInternalAdmin ? handleInternalAdminLogin : handleLogin}
            >
              {isLoading ? <Loading /> : "Log in"}
            </button>
          </div>
        </div>
        <div className='col-8' style={{ paddingRight: 0 }}>
          <div className='login__image'></div>
        </div>
      </div>
    </div>
  );
}

export default Login;
